import { format } from "date-fns";
import React from "react";

import RelativeQueryDatePicker from "./RelativeQueryDatePicker";

import { Group } from "../../../../api/services";

import EmployeePicker, { EmployeeOption, SingleEmployee } from "../../../EmployeePicker";
import GroupPicker from "../../../GroupPicker";
import DatePicker from "../../../Inputs/DatePicker";
import Input from "../../../Inputs/InputText";
// components
import SelectInput, { Result } from "../../../Inputs/SelectInput";
import { QueryValue, RelativeDateValue, getAnniversaryDateLabel, getRelativeDateLabel } from "../../QueryBuilderUtils";
// constants
import {
	ANNIVERSARY_QUERY_OPTIONS,
	DROPDOWN_SELECTION_TYPES,
	GROUP_TYPE,
	NUMERIC_TYPES,
	PEOPLE_SELECTION_TYPES,
} from "../../constants";

export interface QueryValueInputProps {
	fieldType: string;
	onChange?: (queryValue: any) => void;
	value?: QueryValue;
	label?: string;
	options?: { value: string; label: string }[];
}

/*
 * QueryValueInput is a component that reads the input value for different kinds of employee fields on a query.
 * as more types are added, more cases will be added to this component.
 */
export const QueryValueInput = ({ fieldType, onChange, value, label, options = [] }: QueryValueInputProps) => {
	if (onChange) {
		let selector = (
			<Input
				type={NUMERIC_TYPES.includes(fieldType) ? "number" : "text"}
				value={value?.value as string}
				placeholder={NUMERIC_TYPES.includes(fieldType) ? "Enter numeric value" : "Enter a value"}
				onChange={(input) => onChange({ value: input, label: input } as Result)}
			/>
		);

		if (fieldType === "relative") {
			selector = (
				<RelativeQueryDatePicker
					value={value?.value as RelativeDateValue}
					onChange={(input) => {
						const label = getRelativeDateLabel(input);
						onChange({ value: input, label });
					}}
				/>
			);
		}

		if (fieldType === "anniversary" && label !== ANNIVERSARY_QUERY_OPTIONS[0].label) {
			selector = (
				<RelativeQueryDatePicker
					value={value?.value as RelativeDateValue}
					onChange={(input) => {
						const label = getAnniversaryDateLabel(input);
						onChange({ value: input, label });
					}}
					unitOfTimeMap={[
						{ value: "days", label: "Days" },
						{ value: "months", label: "Months" },
					]}
				/>
			);
		}

		if (fieldType === "anniversary" && label === ANNIVERSARY_QUERY_OPTIONS[0].label) {
			selector = <></>;
		}

		if (fieldType === "static") {
			selector = (
				<DatePicker
					value={value?.value as Date}
					onChange={(value) => {
						if (!value) {
							onChange({ value, label: "" });
							return;
						}
						const label = format(value, "MMM d, yyyy");
						onChange({ value, label });
					}}
					placeholder="Select a date"
					hasIcon
				/>
			);
		}

		if (fieldType === GROUP_TYPE) {
			selector = (
				<GroupPicker
					value={{ id: value?.value, name: value?.label } as Group}
					onChange={(group: Group | Group[] | null) => {
						if (Array.isArray(group)) {
							return;
						} else {
							// Handle the case where group is a single object
							onChange({ value: group?.id, label: group?.name } as Result);
						}
					}}
				/>
			);
		}

		if (PEOPLE_SELECTION_TYPES.includes(fieldType)) {
			selector = (
				<EmployeePicker
					onChange={(newEmployeeValue: EmployeeOption | EmployeeOption[]) =>
						onChange(newEmployeeValue as Result)
					}
					value={value as SingleEmployee}
				/>
			);
		}

		if (DROPDOWN_SELECTION_TYPES.includes(fieldType)) {
			selector = (
				<SelectInput
					results={options}
					onChange={(inputValue) => {
						const value = inputValue?.value;
						const label = inputValue?.label;
						onChange({ value, label } as QueryValue);
					}}
					value={value?.value?.toLocaleString() || ""}
				/>
			);
		}

		if (fieldType === "boolean") {
			selector = (
				<SelectInput
					results={[
						{ value: "true", label: "True" },
						{ value: "false", label: "False" },
					]}
					onChange={(inputValue) => {
						const value = inputValue?.value === "true" ? true : false;
						const label = inputValue?.label;
						onChange({ value, label } as QueryValue);
					}}
					value={value?.label?.toLocaleLowerCase() || ""}
				/>
			);
		}

		return (
			<div data-testid="queryValueInput" key={"selection"}>
				{selector}
			</div>
		);
	}
};

export default QueryValueInput;
